import React, { useState } from 'react';
import { Upload, Button, Table, Tag, Select, Space, Typography, App } from 'antd';
import { UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import smsService from '../services/smsService';

const { Dragger } = Upload;
const { Text } = Typography;
const { Option } = Select;

const STATUS_CONFIG = {
  match: { color: 'green', label: 'Matched' },
  nameMismatch: { color: 'gold', label: 'Phone matches, name differs' },
  noMatch: { color: 'red', label: 'No existing match' },
  invalid: { color: 'default', label: 'Invalid row' }
};

// Lets the user upload a CSV of name/surname/phone/department/businessUnit,
// matches each row against existing users by phone number, and — after the
// user resolves any ambiguous/unmatched rows — returns the resulting people
// so the caller can merge them into its own selected-people list.
const CsvPeopleImport = ({ businessUnitOptions = [], onResolved }) => {
  const { message } = App.useApp();
  const [fileList, setFileList] = useState([]);
  const [matching, setMatching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [rows, setRows] = useState([]); // matched rows, annotated with a local `action`/`businessUnitId`

  const beforeUpload = (file) => {
    setFileList([file]);
    return false;
  };

  const handleMatch = async () => {
    if (fileList.length === 0) {
      message.error('Please select a CSV file first');
      return;
    }
    const formData = new FormData();
    formData.append('csvFile', fileList[0]);

    setMatching(true);
    try {
      const result = await smsService.matchCsvPeople(formData);
      if (result.success) {
        setRows(result.data.map(r => ({
          ...r,
          action: r.status === 'match' ? 'useExisting'
            : r.status === 'invalid' ? 'skip'
            : 'skip',
          businessUnitId: r.businessUnitId || undefined
        })));
        setFileList([]);
      } else {
        message.error(result.message || 'Failed to match CSV');
      }
    } finally {
      setMatching(false);
    }
  };

  const updateRow = (rowNumber, changes) => {
    setRows(prev => prev.map(r => r.rowNumber === rowNumber ? { ...r, ...changes } : r));
  };

  const handleApply = async () => {
    const toResolve = rows.filter(r => r.status !== 'invalid' && r.action !== 'skip').map(r => ({
      rowNumber: r.rowNumber,
      action: r.action,
      existingUserId: r.existingUser?._id,
      name: r.name,
      surname: r.surname,
      phone: r.phone,
      department: r.department,
      businessUnitId: r.businessUnitId
    }));

    if (toResolve.length === 0) {
      message.warning('No rows selected to add');
      return;
    }

    const needsBU = toResolve.find(r => r.action === 'create' && !r.businessUnitId);
    if (needsBU) {
      message.error(`Row ${needsBU.rowNumber} needs a business unit selected before it can be created`);
      return;
    }

    setResolving(true);
    try {
      const result = await smsService.resolveCsvPeople(toResolve);
      if (result.success) {
        const { resolved, failed } = result.data;
        if (resolved.length) {
          onResolved(resolved);
          message.success(`Added ${resolved.length} ${resolved.length === 1 ? 'person' : 'people'} from CSV`);
        }
        if (failed.length) {
          message.error(`${failed.length} row(s) could not be added — ${failed.map(f => f.error).join('; ')}`);
        }
        setRows([]);
      } else {
        message.error(result.message || 'Failed to add people');
      }
    } finally {
      setResolving(false);
    }
  };

  const columns = [
    { title: 'Row', dataIndex: 'rowNumber', width: 50 },
    {
      title: 'Name',
      width: 140,
      render: (_, r) => `${r.name || ''} ${r.surname || ''}`.trim()
    },
    { title: 'Phone', dataIndex: 'phone', width: 130 },
    {
      title: 'Department',
      width: 150,
      render: (_, r) => (
        <span style={(r.mismatchFields || []).includes('department') ? { color: '#d48806', fontWeight: 500 } : undefined}>
          {r.department}
        </span>
      )
    },
    {
      title: 'Business Unit',
      width: 150,
      render: (_, r) => (
        <span style={(r.mismatchFields || []).includes('businessUnit') ? { color: '#d48806', fontWeight: 500 } : undefined}>
          {r.businessUnitName || '—'}
        </span>
      )
    },
    {
      title: 'Status',
      width: 220,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Tag color={STATUS_CONFIG[r.status]?.color}>{STATUS_CONFIG[r.status]?.label}</Tag>
          {r.status === 'invalid' && <Text type="secondary" style={{ fontSize: 11 }}>{r.error}</Text>}
          {r.status === 'nameMismatch' && r.existingUser && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Existing: {r.existingUser.name} {r.existingUser.surname}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Action',
      width: 220,
      render: (_, r) => {
        if (r.status === 'invalid') return <Text type="secondary">Skipped</Text>;
        if (r.status === 'match') return <Text type="secondary"><CheckCircleOutlined /> Will be added</Text>;

        const options = r.status === 'nameMismatch'
          ? [
              { value: 'useExisting', label: `Use existing (${r.existingUser?.name} ${r.existingUser?.surname})` },
              { value: 'create', label: 'Create as new person' },
              { value: 'skip', label: 'Skip this row' }
            ]
          : [
              { value: 'create', label: 'Create as new person' },
              { value: 'skip', label: 'Skip this row' }
            ];

        return (
          <Space direction="vertical" size={4}>
            <Select
              size="small"
              style={{ width: 200 }}
              value={r.action}
              onChange={(val) => updateRow(r.rowNumber, { action: val })}
            >
              {options.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
            </Select>
            {r.action === 'create' && (
              <Select
                size="small"
                style={{ width: 200 }}
                placeholder="Select business unit"
                value={r.businessUnitId}
                onChange={(val) => updateRow(r.rowNumber, { businessUnitId: val })}
                status={!r.businessUnitId ? 'error' : undefined}
              >
                {businessUnitOptions.map(bu => <Option key={bu.id} value={bu.id}>{bu.name}</Option>)}
              </Select>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <Dragger
        beforeUpload={beforeUpload}
        onRemove={() => setFileList([])}
        fileList={fileList}
        maxCount={1}
        accept=".csv"
        disabled={matching}
      >
        <p className="ant-upload-drag-icon"><UploadOutlined /></p>
        <p>Click or drag a CSV to bulk-add people</p>
        <p style={{ fontSize: 12, color: '#999' }}>Columns: name, surname, phone, department, businessUnit</p>
      </Dragger>

      {fileList.length > 0 && (
        <Button
          type="primary"
          onClick={handleMatch}
          loading={matching}
          style={{ marginTop: 12 }}
        >
          Match CSV against existing people
        </Button>
      )}

      {rows.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Table
            dataSource={rows}
            columns={columns}
            rowKey="rowNumber"
            size="small"
            pagination={false}
            scroll={{ x: 1090, y: 400 }}
          />
          <Button
            type="primary"
            onClick={handleApply}
            loading={resolving}
            style={{ marginTop: 12 }}
          >
            Add resolved people
          </Button>
        </div>
      )}
    </div>
  );
};

export default CsvPeopleImport;
